import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';
import { activeCouncilNames } from '../config/registry';
import { DEFAULT_CATEGORIES, normaliseSelectedCategories } from '../lib/daCategories';

export const ALL_COUNCIL_FILTERS = activeCouncilNames();

export const DEFAULTS = {
  dashboard_period: 30,
  selected_councils: ALL_COUNCIL_FILTERS,
  dark_mode: true,
  default_map_view: 'byron',
  watch_zone_sort: 'name',
  map_zoom: 10,
  notification_das: true,
  notification_zones: true,
  email_digest: true,
  digest_frequency: 'weekly',
  digest_standard_welcome_pending: false,
  push_alerts: false,
  watch_zone_alerts: true,
  onboarding_completed: false,
  text_size: 'medium',
  show_help_tips: true,
  selected_categories: DEFAULT_CATEGORIES,
  selected_statuses: ["Lodged","Under Assessment","On Exhibition","Approved","Rejected"],
  selected_price_ranges: [],
  filters_collapsed: false,
  dashboard_pipeline_collapsed: false,
  dashboard_pipeline_outcomes_collapsed: false,
  dashboard_status_collapsed: false,
  dashboard_watch_zones_collapsed: false,
};

const LS_KEY_PREFIX = 'sitescope-user-prefs';
const PREF_SELECT = Object.keys(DEFAULTS).join(', ');

function cacheKey(userId) {
  return `${LS_KEY_PREFIX}:${userId || 'guest'}`;
}

function readCache(userId) {
  try { return JSON.parse(localStorage.getItem(cacheKey(userId))) || null; } catch { return null; }
}

function writeCache(userId, prefs) {
  try { localStorage.setItem(cacheKey(userId), JSON.stringify(prefs)); } catch { /* quota */ }
}

function defaultSelectedCouncils(value) {
  return Array.isArray(value) && value.length > 0 ? value : ALL_COUNCIL_FILTERS;
}

export function normaliseUserPreferences(row) {
  const merged = { ...DEFAULTS };
  const repairs = {};
  const source = row && typeof row === 'object' ? row : {};

  for (const key of Object.keys(DEFAULTS)) {
    if (source[key] !== undefined && source[key] !== null) merged[key] = source[key];
  }

  const selectedCouncils = defaultSelectedCouncils(merged.selected_councils);
  if (selectedCouncils !== merged.selected_councils) {
    merged.selected_councils = selectedCouncils;
    repairs.selected_councils = selectedCouncils;
  }

  const { selectedCategories, repaired: categoriesRepaired } = normaliseSelectedCategories(merged.selected_categories);
  if (categoriesRepaired) {
    merged.selected_categories = selectedCategories;
    repairs.selected_categories = selectedCategories;
  }

  return {
    prefs: merged,
    repairs,
    hasRepairs: Object.keys(repairs).length > 0,
  };
}

export function useUserPreferences(user) {
  const [prefs, setPrefs] = useState(() => {
    const cache = normaliseUserPreferences(readCache(user?.id)).prefs;
    // Apply text size immediately from cache — before first paint
    const ts = cache.text_size || 'medium';
    document.documentElement.setAttribute('data-text-size', ts);
    return cache;
  });
  const [loaded, setLoaded] = useState(false);
  // Every account visit owns its own callbacks and pending writes, including A-B-A.
  const userId = user?.id || null;
  const scope = useMemo(() => ({ userId, active: false, timers: new Map() }), [userId]);

  // On login: fetch from Supabase, merge with defaults, overwrite local cache
  useEffect(() => {
    scope.active = true;
    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
      scope.active = false;
      for (const timer of scope.timers.values()) clearTimeout(timer);
      scope.timers.clear();
    };
    const cached = normaliseUserPreferences(readCache(scope.userId)).prefs;
    setPrefs(cached);
    document.documentElement.setAttribute('data-text-size', cached.text_size || 'medium');
    setLoaded(false);
    if (!scope.userId) { setLoaded(true); return cleanup; }

    (async () => {
      // Timeout-guarded: if this read wedges (Safari resume, flaky network), we keep
      // the cached/default prefs and still flip `loaded` so the app never waits on it.
      const { data, error } = await readWithTimeout(
        signal => supabase
          .from('user_preferences')
          .select(PREF_SELECT)
          .eq('user_id', scope.userId)
          .single()
          .abortSignal(signal),
        { label: 'user_preferences_load' }
      );

      if (cancelled) return;

      if (error && error.isTimeout) {
        console.warn('[useUserPreferences] load timed out — using cached prefs');
      } else if (error && error.code === 'PGRST116') {
        // No row — create one with defaults
        const row = { user_id: scope.userId, ...DEFAULTS };
        await readWithTimeout(
          signal => supabase.from('user_preferences').insert(row).abortSignal(signal),
          { label: 'user_preferences_insert' }
        );
        if (cancelled) return;
        setPrefs({ ...DEFAULTS });
        writeCache(scope.userId, { ...DEFAULTS });
      } else if (data) {
        const { prefs: merged, repairs, hasRepairs } = normaliseUserPreferences(data);
        setPrefs(merged);
        writeCache(scope.userId, merged);
        if (hasRepairs) {
          supabase.from('user_preferences')
            .upsert({ user_id: scope.userId, ...repairs }, { onConflict: 'user_id' })
            .then(({ error: repairError }) => {
              if (repairError) console.error('repairPrefs error:', repairError.message);
            });
        }
      }
      setLoaded(true);
    })();

    return cleanup;
  }, [scope]);

  // Local updates and writes belong to this account visit only.
  const updateLocal = useCallback((updates) => {
    if (!scope.active) return;
    setPrefs(prev => {
      if (!scope.active) return prev;
      const next = { ...prev, ...updates };
      writeCache(scope.userId, next);
      return next;
    });
  }, [scope]);

  const savePref = useCallback((key, value) => {
    if (!scope.active) return;
    updateLocal({ [key]: value });
    if (!scope.userId) return;

    clearTimeout(scope.timers.get(key));
    scope.timers.set(key, setTimeout(() => {
      scope.timers.delete(key);
      if (!scope.active) return;
      supabase.from('user_preferences')
        .upsert({ user_id: scope.userId, [key]: value }, { onConflict: 'user_id' })
        .then(({ error }) => { if (error) console.error('savePref error:', key, error.message); });
    }, 300));
  }, [scope, updateLocal]);

  const savePrefs = useCallback((updates) => {
    if (!scope.active) return;
    // A newer batch must not be overwritten by an older queued single-field save.
    for (const key of Object.keys(updates)) {
      clearTimeout(scope.timers.get(key));
      scope.timers.delete(key);
    }
    updateLocal(updates);
    if (!scope.userId) return;
    supabase.from('user_preferences')
      .upsert({ user_id: scope.userId, ...updates }, { onConflict: 'user_id' })
      .then(({ error }) => { if (error) console.error('savePrefs error:', error.message); });
  }, [scope, updateLocal]);

  return { prefs, savePref, savePrefs, loaded };
}
